import { B2B_PORTAL_CSRF_TOKEN } from "redmond";
import { getCookie } from "./cookieUtil";

const SESSION_TOKEN_KEY = "b2bportal-session-token";
const CSRF_TOKEN_KEY = "b2bportal-csrf-token";

/**
 * Auth token store for third-party iframe embeds (Brex).
 *
 * Normally both tokens travel as cookies: `Hopper-Session` (httpOnly) and `H-Csrf-Token`.
 * Inside a third-party iframe the browser blocks both, so we fall back to reading them
 * from the login response body and sending them as headers instead. Tysons reads the
 * session from a `Hopper-Session` header when the cookie is absent, and only ever reads
 * CSRF from the `H-Csrf-Token` header.
 *
 * The CSRF token additionally matters to the frontend itself: `GET /userInfo` echoes back
 * whatever `H-Csrf-Token` header it received, and the corporate app treats a truthy
 * `sessionInfo.csrfToken` as its "logged in" signal. Without the header, userInfo returns
 * an empty string and the app renders nothing.
 *
 * `sessionStorage`, not memory: the iframe can reload and `GET /userInfo` does not reissue
 * these, so an in-memory copy would log the user out on any reload. Scoped per-tab, and
 * cleared on logout / 401.
 */
const read = (key: string): string | undefined => {
  try {
    return sessionStorage.getItem(key) || undefined;
  } catch {
    // Storage can throw in hardened/partitioned iframe contexts; fall back to cookie auth.
    return undefined;
  }
};

const write = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // No-op: absent a stored token the interceptors simply omit the header.
  }
};

const remove = (key: string) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // No-op.
  }
};

export const getStoredSessionToken = () => read(SESSION_TOKEN_KEY);

export const setStoredSessionToken = (token: string) =>
  write(SESSION_TOKEN_KEY, token);

export const getStoredCsrfToken = () => read(CSRF_TOKEN_KEY);

export const setStoredCsrfToken = (token: string) =>
  write(CSRF_TOKEN_KEY, token);

export const clearStoredSessionToken = () => {
  remove(SESSION_TOKEN_KEY);
  remove(CSRF_TOKEN_KEY);
};

/**
 * Auth headers for API clients that don't run through `mcLeanAxiosInstance` and would
 * otherwise depend on the `Hopper-Session` / `H-Csrf-Token` cookies — which the browser
 * blocks inside a third-party iframe (Brex). Mirrors the mcLeanAxiosInstance request
 * interceptor: CSRF is read cookie-first with a sessionStorage fallback; the session token
 * lives only in sessionStorage (the `Hopper-Session` cookie is httpOnly, so unreadable).
 *
 * Each header is omitted when its token is absent, so cookie-based (non-iframe) callers —
 * whose sessionStorage fallback is empty — send exactly what they send today and are
 * unaffected.
 */
export const getIframeAuthHeaders = (): Record<string, string> => {
  const csrfToken = getCookie(B2B_PORTAL_CSRF_TOKEN) ?? getStoredCsrfToken();
  const sessionToken = getStoredSessionToken();
  return {
    ...(csrfToken ? { "H-Csrf-Token": csrfToken } : {}),
    ...(sessionToken ? { "Hopper-Session": sessionToken } : {}),
  };
};
